/**
 * @component OrderWidget
 * @description
 * Displays the user's orders for a selected calendar year, divided into status segments.
 *
 * ---
 * **Features**
 * - Responsive two-column layout:
 *   - **Left panel:** Placeholder for the user’s capacity summary (SIB-137).
 *   - **Right panel:** Segmented order view with tabs for "Offen", "Freigegeben",
 *     "Abgelehnt", and "Archiv".
 * - **Year selector** beside the title:
 *   - Minimum = user registration year.
 *   - Maximum = current year, or next year if after November 1.
 * - **Neue Bestellung** button:
 *   - Triggers the `onCreateOrder(year)` callback (placeholder for the Create-Order popup).
 * - **Order list:**
 *   - Orders rendered vertically and grouped by last-update date.
 *   - Each order shows customer name, date range, and status with color dot.
 * - **Tabs:**
 *   - Variant `subtle` with reversed color scheme per design.
 *   - Active tab uses white background; inactive tabs use the muted color.
 * - **Empty state:**
 *   - Shows a “Text Select” icon and explanatory message inside a subtle panel.
 * - Uses Chakra UI v3.28 System API components exclusively.
 *
 * ---
 * **Props**
 * @prop {number} [registrationYear=2023]
 *   Earliest selectable year in the year selector.
 * @prop {Object} [orders]
 *   Lists of orders per tab category (`offen`, `freigegeben`, `abgelehnt`, `archiv`).
 * @prop {Function} [onCreateOrder]
 *   Callback invoked when the "Neue Bestellung" button is clicked. Receives the selected year.
 *
 * ---
 * **Usage**
 * ```tsx
 * <OrderWidget
 *   registrationYear={2023}
 *   orders={mockOrders}
 *   onCreateOrder={(year) => console.log('Create order for', year)}
 * />
 * ```
 *
 * ---
 * **References**
 * - SIB-134 — [Order] [FE] Implement Order Widget
 * - Chakra UI v3.28 System API docs: https://chakra-ui.com/docs/components/concepts/overview
 */

import {
  Box,
  Badge,
  Button,
  Card,
  Flex,
  HStack,
  Separator,
  Stat,
  Tabs,
  Text,
  VStack,
  chakra,
} from '@chakra-ui/react'
import { useCallback, useEffect, useState, forwardRef, useImperativeHandle, useMemo } from 'react'
import {
  LuArrowDown,
  LuChevronLeft,
  LuChevronRight,
  LuFileClock,
  LuInfo,
  LuTextSelect,
} from 'react-icons/lu'
import { getOrders, getWorkload } from '@/api/orders.api'
import { useUserStore } from '@/stores/user.store'
import { Tooltip } from '@/components/ui/tooltip'
import OrderCreateDialog from '@/components/order/CreateOrderDialog'
import OrderItem from './OrderItem'
import { hoursToUnits, mapApiStatusToUiStatus } from '@/helper/order.helper'
import {
  getAllBanks,
  getBanks,
  getProfileSettings,
  getUserOrderCapacity,
} from '@/api/settings.api'
import { usePlanningYears } from '@/hooks/usePlanningYears'
import type { UserProfile } from '@/common/user.common'
// import { useOnboardingStore } from '@/stores/onboarding.store'
import { toaster } from '../ui/toaster/toasterInstance'
import type { Bank } from '@/common/settings.common'
import { isUnionInvestmentBank } from '@/common/unionBank.common'
import React from 'react'
import { EmptyStateCard } from '@/components/widget-wrappers/EmptyStateCard'
import { WidgetShell } from '@/components/widget-wrappers/WidgetShell'

export interface OrderSummary {
  id: string | number
  customer: string
  dateRange: string
  status: string
  buckets?: {
    serviceCode: string
    plannedHours: number
  }[]
  customerId?: number
  customerName?: string
  bankName: string
  groupDate?: string
  attachmentCount?: number
}

export interface OrderWidgetHandle {
  openCreateOrder: () => void
}

interface OrderWidgetProps {
  registrationYear?: number
  hasCompletedCapacitySetup?: boolean
  hasUnionSettingsSetup: boolean
  hasInvoiceSettingsSetup?: boolean
  _orders?: {
    offen?: OrderSummary[]
    freigegeben?: OrderSummary[]
    abgelehnt?: OrderSummary[]
    archiv?: OrderSummary[]
  }

  onCreateOrder?: (year: number) => void
  onOrdersLoaded?: (orders: OrderSummary[]) => void
}
type WorkloadBucket = {
  remainingCapacityHours: number
  serviceCode?: string
  remainingApprovedOrderHours?: number
}
// const HPU: Record<string, number> = { TOJ: 2.5, CONSULTING: 1, TRAINING: 8 }
type UnitsQuantities = { toj?: number; beratung?: number; schulung?: number; pilot?: number }

export const OrderWidget = forwardRef<OrderWidgetHandle, OrderWidgetProps>(
  (
    {
      registrationYear = 2025,
      _orders = {},
      onCreateOrder,
      onOrdersLoaded,
      hasCompletedCapacitySetup,
      hasUnionSettingsSetup,
      hasInvoiceSettingsSetup,
    },
    ref
  ) => {
    const currentYear = new Date().getFullYear()
    const minYear = currentYear - 1
    // The latest year open for planning, as Union set it in the backoffice (SIB-928).
    const { openYears } = usePlanningYears()
    const maxYear = Math.max(currentYear, ...openYears)
    const [year, setYear] = useState(currentYear)

    const [isCreateDialogOpen, setCreateDialogOpen] = useState(false)
    const [banks, setBanks] = useState<Bank[]>([]) // full DTO
    const [customers, setCustomers] = useState<Bank[]>([]) // optional fallback
    const [loadingBanks, setLoadingBanks] = useState(true)
    const [bankError, setBankError] = useState<string | null>(null)
    const [orders, setOrders] = useState<OrderWidgetProps['_orders']>(_orders)
    // const onboarding = useOnboardingStore()

    type CapacityMap = {
      TOJ: number
      Beratung: number
      Schulung: number
      Pilot: number
    }

    const openCreateOrder = useCallback(() => {
      if (!hasCompletedCapacitySetup) {
        toaster.warning({
          title: 'Kapazitätseinstellungen unvollständig',
          description:
            'Bitte richten Sie Ihre Kapazitätseinstellungen ein, bevor Sie Bestellungen erstellen.',
          action: {
            label: 'Einrichten',
            onClick: () => (location.pathname = '/settings/orders'),
          },
        })
        return
      }
      if (!hasInvoiceSettingsSetup) {
        toaster.warning({
          title: 'Rechnungseinstellungen unvollständig',
          description:
            'Bitte richten Sie Ihre Rechnungseinstellungen ein, bevor Sie Bestellungen erstellen.',
          action: {
            label: 'Einrichten',
            onClick: () => (location.pathname = '/settings/invoice'), // preferred
          },
        })
        return
      }

      if (!hasUnionSettingsSetup) {
        toaster.warning({
          title: 'Leistungskonfiguration unvollständig',
          description:
            'Bitte richten Sie Ihre Leistungskonfiguration ein, bevor Sie Bestellungen erstellen.',
          action: {
            label: 'Einrichten',
            onClick: () => (location.pathname = '/settings/bank'),
          },
        })
        return
      }
      setCreateDialogOpen(true)
      onCreateOrder?.(year)
    }, [
      onCreateOrder,
      year,
      hasCompletedCapacitySetup,
      hasUnionSettingsSetup,
      hasInvoiceSettingsSetup,
    ])

    useImperativeHandle(
      ref,
      () => ({
        openCreateOrder,
      }),
      [openCreateOrder]
    )

    const [capacity, setCapacity] = useState<CapacityMap | null>(null)
    const user = useUserStore(s => s.user)
    const [profile, setProfile] = useState<UserProfile | null>(null)
    const [workload, setWorkload] = useState<Record<string, number> | null>(null)
    const [highlightedOrderUnits, setHighlightedOrderUnits] = useState<UnitsQuantities | null>(
      null
    )
    useEffect(() => {
      async function load() {
        try {
          setLoadingBanks(true)
          const response = await getBanks()

          // API returns: { items: BankDto[] }
          const list = response.items ?? []
          setBanks(list)
          setCustomers(list) // still used for fallback only
        } catch (err: any) {
          setBankError(err.message || 'Failed to load banks')
        } finally {
          setLoadingBanks(false)
        }
      }

      load()
    }, [])
    const unionBank = useMemo(() => {
      return banks.find(isUnionInvestmentBank)
    }, [banks])

    const [unionChildBanks, setUnionChildBanks] = useState<Bank[]>([])
    // An empty dropdown used to mean three different things — still loading, genuinely no
    // results, or the request failed — and looked identical in all three. The list arrives
    // only after four sequential round trips (banks → find Union → children page 1 → page 2),
    // so "still loading" is the common case and the one that reads as a bug.
    const [childBanksLoading, setChildBanksLoading] = useState(false)
    const [childBanksFailed, setChildBanksFailed] = useState(false)

    useEffect(() => {
      if (!unionBank?.id) {
        setUnionChildBanks([])
        setChildBanksLoading(false)
        setChildBanksFailed(false)
        return
      }

      let cancelled = false
      // Cleared alongside the loading flag, not just in the no-union branch: the previous
      // union's recipients would otherwise stay on screen as though they were current, and
      // a non-empty list means Combobox.Empty never renders — so the loading message would
      // be masked by exactly the stale data it was added to explain.
      setUnionChildBanks([])
      setChildBanksLoading(true)
      setChildBanksFailed(false)

      // Use getAllBanks instead of associatedEntities, which only returns id/name/active
      // and not the postal/blzNum needed for bank search
      getAllBanks({ parentId: unionBank.id })
        .then(list => {
          if (cancelled) return
          setUnionChildBanks(list)
          setChildBanksLoading(false)
        })
        .catch(() => {
          if (cancelled) return
          setUnionChildBanks([])
          setChildBanksFailed(true)
          setChildBanksLoading(false)
        })

      return () => {
        cancelled = true
      }
    }, [unionBank?.id])

    const unionAssociatedEntities = useMemo(() => {
      return unionChildBanks.map(entity => ({
        id: entity.id,
        name: entity.name,
        name2: entity.name2,
        name3: entity.name3,
        active: entity.active,
        postal: entity.postal,
        blzNum: entity.blzNum,
      }))
    }, [unionChildBanks])

    const activeUnionAssociatedEntities = useMemo(() => {
      return unionAssociatedEntities.filter(entity => entity.active !== false)
    }, [unionAssociatedEntities])

    const handleYearChange = (dir: 'prev' | 'next') => {
      setYear(prev => {
        if (dir === 'prev' && prev > minYear) return prev - 1
        if (dir === 'next' && prev < maxYear) return prev + 1
        return prev
      })
    }
    // const handleCreateOrder = () => {
    //   setCreateDialogOpen(true)
    //   onCreateOrder?.(year)
    // }

    const orderCounts = useMemo(() => {
      return {
        offen: orders?.offen?.length ?? 0,
        freigegeben: orders?.freigegeben?.length ?? 0,
        abgelehnt: orders?.abgelehnt?.length ?? 0,
        // archiv: orders?.archiv?.length ?? 0, // if you later re-enable Archiv tab
      }
    }, [orders])

    const fetchOrders = useCallback(async () => {
      const orders = await getOrders({ year })
      const yearItems = orders.items[year]

      // ✅ Fix: Year has NO orders → show empty state
      if (!yearItems) {
        setOrders({
          offen: [],
          freigegeben: [],
          abgelehnt: [],
          archiv: [],
        })
        onOrdersLoaded?.([])
        return
      }
      const summaryList = await Promise.all(
        Object.values(orders.items[year])
          .flat()
          .map(async order => {
            const receiver = unionAssociatedEntities.find(
              e => Number(e.id) === Number(order.customerId)
            )
            const receiverName = receiver
              ? [receiver.name, receiver.name2, receiver.name3].filter(Boolean).join(' – ')
              : ''

            return {
              id: order.id,
              customer: receiverName,
              customerId: order.customerId,
              customerName: receiverName,
              bankName: order.bankName ?? '',
              dateRange: `${new Date(order.startTime).toLocaleDateString('de-DE')} - ${new Date(order.endTime).toLocaleDateString('de-DE')}`,
              status: order.status,
              // buckets: order.buckets,
              buckets: (order.buckets ?? []).map((b: any) => ({
                serviceCode: b.serviceCode ?? b.serviceType,
                plannedHours: b.plannedHours ?? 0,
              })),

              groupDate: new Date(order.updatedAt).toLocaleDateString(),
              attachmentCount: order.attachmentCount, // <- now defined
            } as OrderSummary
          })
      )

      const grouped: OrderWidgetProps['_orders'] = {}

      summaryList.forEach(summary => {
        const statusKey =
          summary.status === 'SUBMITTED' || summary.status === 'CORRECTED'
            ? 'offen'
            : summary.status === 'APPROVED'
              ? 'freigegeben'
              : summary.status === 'REJECTED'
                ? 'abgelehnt'
                : ['COMPLETED', 'DELETED'].includes(summary.status)
                  ? 'archiv'
                  : 'offen'

        if (!grouped[statusKey]) grouped[statusKey] = []
        grouped[statusKey]!.push(summary)
      })

      setOrders(grouped)
      onOrdersLoaded?.(summaryList)
    }, [year, unionAssociatedEntities])

    type OrderGroupKey = 'offen' | 'freigegeben' | 'abgelehnt' | 'archiv'

    const handleAttachmentCountChange = useCallback((orderId: number, count: number) => {
      setOrders(prev => {
        if (!prev) return prev

        const updated = { ...prev }

        ;(Object.keys(updated) as OrderGroupKey[]).forEach(key => {
          updated[key] = updated[key]?.map((order: OrderSummary) =>
            Number(order.id) === Number(orderId) ? { ...order, attachmentCount: count } : order
          )
        })

        return updated
      })
    }, [])

    useEffect(() => {
      async function loadProfile() {
        try {
          const p = await getProfileSettings()
          setProfile(p)
        } catch (err) {
          console.error('Cannot load user profile', err)
        }
      }

      loadProfile()
    }, [])
    useEffect(() => {
      fetchOrders()
    }, [fetchOrders])

    useEffect(() => {
      async function loadCapacity() {
        if (!user?.id) return

        const result = await getUserOrderCapacity(user.id)

        const mapped = {
          TOJ: result.settings.find(s => s.service.code === 'TOJ')?.capacity ?? 0,
          Beratung: result.settings.find(s => s.service.code === 'CONSULTING')?.capacity ?? 0,
          Schulung: result.settings.find(s => s.service.code === 'TRAINING')?.capacity ?? 0,
          Pilot: result.settings.find(s => s.service.code === 'PILOT')?.capacity ?? 0,
        }

        setCapacity(mapped)
      }

      loadCapacity()
    }, [user?.id])
    useEffect(() => {
      async function loadWorkload() {
        if (!user?.id) return

        const from = `${year}-01-01T00:00:00`
        const to = `${year}-12-31T23:59:59`

        try {
          // const bankId = banks[0].id; // UNION Investment, ID=1
          // const bankId = 1; // UNION Investment, ID=1

          const result = await getWorkload({
            userId: user.id,
            // bankId,
            from,
            to,
          })

          const map: Record<string, number> = {
            TOJ: 0,
            CONSULTING: 0,
            TRAINING: 0,
          }

          const buckets = (result.buckets ?? []) as WorkloadBucket[]
          // const buckets =
          //   (order.buckets ?? []).map((b: any) => ({
          //     serviceCode: b.serviceCode ?? b.serviceType, // accept both
          //     plannedHours: b.plannedHours ?? 0,
          //   })) ?? []

          buckets.forEach(b => {
            const hours = b.remainingCapacityHours ?? 0
            // map[b.serviceCode] = hoursToUnits(b.serviceCode, hours)
            const code = b.serviceCode ?? (b as any).serviceType
            if (!code) return
            map[code] = hoursToUnits(code, hours)
          })

          setWorkload(map)
        } catch (err) {
          console.error('Failed to load workload', err)
        }
      }

      loadWorkload()
    }, [user?.id, year])
    // === Placeholder for SIB-137 (Kontingentinfo) ===
    const renderPlaceholderLeft = (username?: string) => {
      const vereinbart = capacity ?? { TOJ: 0, Beratung: 0, Schulung: 0, Pilot: 0 }

      const baseÜbrig = {
        TOJ: workload?.TOJ ?? 0,
        Beratung: workload?.CONSULTING ?? 0,
        Schulung: workload?.TRAINING ?? 0,
        Pilot: workload?.PILOT ?? 0,
      }

      const übrig = highlightedOrderUnits
        ? {
            TOJ: baseÜbrig.TOJ - (highlightedOrderUnits.toj ?? 0),
            Beratung: baseÜbrig.Beratung - (highlightedOrderUnits.beratung ?? 0),
            Schulung: baseÜbrig.Schulung - (highlightedOrderUnits.schulung ?? 0),
            Pilot: baseÜbrig.Pilot - (highlightedOrderUnits.pilot ?? 0),
          }
        : baseÜbrig

      const hasPilotCapacity = vereinbart.Pilot > 0 || (workload?.PILOT ?? 0) > 0
      const categories = hasPilotCapacity
        ? ['TOJ', 'Beratung', 'Schulung', 'Pilot']
        : ['TOJ', 'Beratung', 'Schulung']

      return (
        <Card.Root flex="1" variant="subtle">
          <Card.Header>
            <Card.Title>{username}</Card.Title>
            <HStack align="center" gap="1">
              <Card.Description>Leistungskontingent</Card.Description>

              <Tooltip
                content={
                  <Box maxW="250px">
                    <Text>Leistungskontingent</Text>
                    <Text>
                      In der linken Spalte sehen Sie die Leistungen, die Sie mit der Union
                      Investment für das ausgewählte Kalenderjahr vereinbart haben.
                    </Text>
                    <Text>
                      In der rechten Spalte sind die Leistungen, die Sie noch nicht in
                      freigegebenen Bestellungen reserviert haben. Diese können Sie also noch in
                      weiteren Bestellungen verbrauchen.
                    </Text>
                    <Text>Sie sollten das vereinbarte Kontingent jedes Jahr aufbrauchen.</Text>
                  </Box>
                }
              >
                <chakra.span cursor="pointer">
                  <LuInfo />
                </chakra.span>
              </Tooltip>
            </HStack>
          </Card.Header>
          <Card.Body>
            <VStack align="stretch" gap="1">
              {categories.map((label, idx) => {
                const vereinbartKey = label as keyof typeof vereinbart
                const übrigKey = label as keyof typeof übrig

                const vereinbartValue = vereinbart[vereinbartKey] ?? 0
                const übrigValue = übrig[übrigKey] ?? 0

                const used = vereinbartValue - übrigValue
                const isNegative = übrigValue < 0

                return (
                  <Box key={label}>
                    <Text>{label}</Text>

                    <HStack justify="space-between" align="start">
                      {/* Vereinbart (left) */}
                      <Stat.Root>
                        <Stat.ValueText>{vereinbartValue}</Stat.ValueText>
                        <Stat.HelpText>Vereinbart</Stat.HelpText>
                      </Stat.Root>

                      {/* Übrig + Tag (right) */}
                      <Stat.Root textAlign="right">
                        <HStack gap="2" justify="flex-end" align="center">
                          <Stat.ValueText>{übrigValue}</Stat.ValueText>
                          {highlightedOrderUnits && (
                            <Badge colorPalette={isNegative ? 'red' : 'green'} variant="subtle">
                              <LuArrowDown /> {used}
                            </Badge>
                          )}
                        </HStack>
                        <Stat.HelpText>Übrig</Stat.HelpText>
                      </Stat.Root>
                    </HStack>

                    {idx < categories.length - 1 && <Separator my="3" borderColor="border" />}
                  </Box>
                )
              })}
            </VStack>
          </Card.Body>
        </Card.Root>
      )
    }

    // === Empty State ===
    const renderEmpty = (label: string) => (
      <EmptyStateCard
        key={label}
        minH="370px"
        icon={<LuTextSelect />}
        title={`Keine ${label} Bestellungen`}
        description={
          <>
            Für das ausgewählte Kalenderjahr liegen keine offenen Bestellungen vor. Bitte wählen
            Sie ein anderes Jahr aus oder geben Sie ihre erste Bestellung auf.
          </>
        }
      />
    )

    // === Tab Content ===
    const renderTab = (
      list?: OrderSummary[],
      label?: string,
      isOpenTab?: boolean,
      readOnly?: boolean
    ) => {
      if (!list || list.length === 0) return renderEmpty(label || 'offenen')

      const chunks: Array<{ date?: string; items: OrderSummary[] }> = []
      for (const item of list) {
        const last = chunks[chunks.length - 1]
        const date = item.groupDate || ''
        if (!last || last.date !== date) chunks.push({ date, items: [item] })
        else last.items.push(item)
      }

      if (loadingBanks) {
        return <div style={{ padding: 20 }}>Lade Leistungsempfänger…</div>
      }

      if (bankError) {
        return <div style={{ padding: 20, color: 'red' }}>{bankError}</div>
      }

      return (
        <VStack align="stretch" gap="3">
          {chunks.map((chunk, idx) => (
            <Box key={idx}>
              {chunk.date && (
                <Text color="fg.muted" textStyle="metadata" mb="2">
                  {chunk.date}
                </Text>
              )}
              <VStack align="stretch" gap="3">
                {chunk.items.map(order => (
                  <OrderItem
                    key={order.id}
                    orderId={Number(order.id)}
                    readOnly={readOnly}
                    bankName={order.customerName || order.bankName}
                    periodText={order.dateRange}
                    status={mapApiStatusToUiStatus(order.status)}
                    ordered={{
                      toj: order.buckets?.find(b => b.serviceCode === 'TOJ')?.plannedHours ?? 0,
                      beratung:
                        order.buckets?.find(b => b.serviceCode === 'CONSULTING')?.plannedHours ??
                        0,
                      schulung:
                        order.buckets?.find(b => b.serviceCode === 'TRAINING')?.plannedHours ?? 0,
                      pilot:
                        order.buckets?.find(b => b.serviceCode === 'PILOT')?.plannedHours ?? 0,
                    }}
                    attachmentCount={order.attachmentCount ?? 0}
                    onAttachmentCountChange={handleAttachmentCountChange} // <-- add this
                    onToggleExpand={
                      isOpenTab
                        ? (expanded, ordered) => {
                            if (expanded && ordered) {
                              setHighlightedOrderUnits({
                                toj: hoursToUnits('TOJ', ordered.toj ?? 0),
                                beratung: hoursToUnits('CONSULTING', ordered.beratung ?? 0),
                                schulung: hoursToUnits('TRAINING', ordered.schulung ?? 0),
                                pilot: hoursToUnits('PILOT', ordered.pilot ?? 0),
                              })
                            } else {
                              setHighlightedOrderUnits(null)
                            }
                          }
                        : undefined
                    }
                    onAcceptCorrection={fetchOrders}
                    onRejectCorrection={fetchOrders}
                    onAfterDelete={fetchOrders}
                  />
                ))}
              </VStack>
            </Box>
          ))}
        </VStack>
      )
    }

    return (
      <WidgetShell
        id="orders-widget"
        h="600px"
        w="full"
        maxW="100%"
        overflow="hidden"
        title="Bestellung"
        headerAfterTitle={
          <HStack>
            <Button
              aria-label="Vorheriges Jahr"
              variant="ghost"
              onClick={() => handleYearChange('prev')}
              disabled={year <= registrationYear}
            >
              <LuChevronLeft />
            </Button>

            <Text data-testid="order-widget-year">{year}</Text>

            <Button
              aria-label="Nächstes Jahr"
              variant="ghost"
              onClick={() => handleYearChange('next')}
              disabled={year >= maxYear}
            >
              <LuChevronRight />
            </Button>
          </HStack>
        }
        headerRight={
          <Button
            data-testid="new-order-button"
            onClick={openCreateOrder}
            variant="outline"
            colorPalette="gray"
          >
            <LuFileClock />
            Neue Bestellung
          </Button>
        }
      >
        {/* === Main Layout === */}
        <Flex gap="8" direction={{ base: 'column', md: 'row' }} align="stretch">
          {renderPlaceholderLeft(profile ? `${profile.firstName} ${profile.lastName}` : '–')}

          <Box flex="2">
            <Tabs.Root defaultValue="offen" variant="subtle" colorPalette="primary" width="full">
              {/* Segmented tab container */}
              <Flex
                overflowX="auto"
                css={{
                  WebkitOverflowScrolling: 'touch',
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': { display: 'none' },
                }}
              >
                <Tabs.List maxW="full" gap="1" flexWrap="nowrap" whiteSpace="nowrap">
                  <Tabs.Trigger value="offen" flex="0 0 auto">
                    {/* Offen */}
                    <Flex align="center" gap="2">
                      <Text>Offen</Text>
                      <Badge size="sm" variant="subtle">
                        {orderCounts.offen}
                      </Badge>
                    </Flex>
                  </Tabs.Trigger>

                  <Tabs.Trigger value="freigegeben" flex="0 0 auto">
                    {/* Freigegeben */}
                    <Flex align="center" gap="2">
                      <Text>Freigegeben</Text>
                      <Badge size="sm" variant="subtle">
                        {orderCounts.freigegeben}
                      </Badge>
                    </Flex>
                  </Tabs.Trigger>

                  <Tabs.Trigger value="abgelehnt" flex="0 0 auto">
                    {/* Abgelehnt */}
                    <Flex align="center" gap="2">
                      <Text>Abgelehnt</Text>
                      <Badge size="sm" variant="subtle">
                        {orderCounts.abgelehnt}
                      </Badge>
                    </Flex>
                  </Tabs.Trigger>
                </Tabs.List>
              </Flex>

              <Tabs.Content value="offen">
                {/*
                  Cap the scroll area to what actually fits below the header/tabs inside the
                  600px card (which is overflow:hidden). The old maxH="600px" was the full
                  card height, so the bottom of an expanded order — including the "Korrektur
                  akzeptieren/ablehnen" buttons — was pushed past the card edge and clipped,
                  unreachable however far the user scrolled (SIB-856). The ~140px subtracted
                  is the card padding + header row + tab list above this panel.
                */}
                <Box maxH="calc(600px - 140px)" overflowY="auto" pr="1">
                  {renderTab(orders?.offen || [], 'offenen', true)}
                </Box>
              </Tabs.Content>

              <Tabs.Content value="freigegeben">
                <Box maxH="360px" overflowY="auto" pr="1">
                  {renderTab(orders?.freigegeben || [], 'freigegebenen', false)}
                </Box>
              </Tabs.Content>

              <Tabs.Content value="abgelehnt">
                <Box maxH="360px" overflowY="auto" pr="1">
                  {renderTab(orders?.abgelehnt || [], 'abgelehnten', false)}
                </Box>
              </Tabs.Content>

              <Tabs.Content value="archiv">
                <Box maxH="360px" overflowY="auto" pr="1">
                  {renderTab(orders?.archiv || [], 'archivierten', false, true)}
                </Box>
              </Tabs.Content>
            </Tabs.Root>
          </Box>
        </Flex>
        <OrderCreateDialog
          isOpen={isCreateDialogOpen}
          onOpenChange={setCreateDialogOpen}
          initialCapacity={{
            toj: workload?.TOJ ?? 0,
            consulting: workload?.CONSULTING ?? 0,
            training: workload?.TRAINING ?? 0,
            pilot: workload?.PILOT ?? 0,
          }}
          banks={unionBank ? [unionBank] : []}
          customers={activeUnionAssociatedEntities}
          customersLoading={childBanksLoading}
          customersFailed={childBanksFailed}
          onSubmit={async () => {
            return { ok: true }
          }}
          onOrderCreated={fetchOrders}
        />
      </WidgetShell>
    )
  }
)

// export default OrderWidget

export default React.memo(OrderWidget)
